<template>
  <div class="life-page">
    <section class="life-panel">
      <div class="life-panel__head">
        <h2 class="life-panel__title">周边游商品管理</h2>
        <el-button type="primary" icon="el-icon-plus" size="small" @click="goEdit()">新增商品</el-button>
      </div>
      <div class="life-filter">
        <el-input v-model="filters.productName" placeholder="商品名称" clearable style="width: 180px" @keyup.enter.native="search" />
        <el-select v-model="filters.productType" placeholder="商品类型" clearable style="width: 140px">
          <el-option label="景区" value="SCENIC" />
          <el-option label="线路" value="ROUTE" />
        </el-select>
        <el-select v-model="filters.saleStatus" placeholder="销售状态" clearable style="width: 140px">
          <el-option label="草稿" value="DRAFT" />
          <el-option label="已上架" value="ON_SALE" />
          <el-option label="已下架" value="OFF_SALE" />
          <el-option label="已归档" value="ARCHIVED" />
        </el-select>
        <el-select v-model="filters.recommended" placeholder="是否推荐" clearable style="width: 120px">
          <el-option label="是" value="Y" />
          <el-option label="否" value="N" />
        </el-select>
        <el-button type="primary" icon="el-icon-search" @click="search">查询</el-button>
        <el-button icon="el-icon-refresh" @click="reset">重置</el-button>
      </div>
    </section>

    <section class="life-panel">
      <el-table v-loading="loading" :data="rows" border>
        <el-table-column prop="productName" label="商品名称" min-width="160" />
        <el-table-column label="类型" width="90" align="center">
          <template slot-scope="scope">{{ typeLabel(scope.row.productType) }}</template>
        </el-table-column>
        <el-table-column prop="departureCity" label="出发地" width="100" />
        <el-table-column label="起售价" width="100" align="center">
          <template slot-scope="scope">
            <span class="life-money">{{ fenToYuan(scope.row.minSalePriceFen) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="futureBatchCount" label="可售批次" width="90" align="center" />
        <el-table-column prop="futureAvailableStock" label="可售库存" width="90" align="center" />
        <el-table-column label="推荐" width="80" align="center">
          <template slot-scope="scope">{{ scope.row.recommended === 'Y' ? '是' : '否' }}</template>
        </el-table-column>
        <el-table-column label="销售状态" width="100" align="center">
          <template slot-scope="scope">
            <el-tag size="small" :type="statusTag(scope.row.saleStatus)">{{ statusLabel(scope.row.saleStatus) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="sortOrder" label="排序" width="70" align="center" />
        <el-table-column prop="createTime" label="更新时间" width="170" />
        <el-table-column label="操作" width="300" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" @click="goEdit(scope.row)">编辑</el-button>
            <el-button type="text" @click="handleCopy(scope.row)">复制</el-button>
            <el-button type="text" @click="preview(scope.row)">预览</el-button>
            <el-button v-if="scope.row.saleStatus !== 'ON_SALE'" type="text" @click="handlePublish(scope.row)">上架</el-button>
            <el-button v-if="scope.row.saleStatus === 'ON_SALE'" type="text" @click="handleOffline(scope.row)">下架</el-button>
            <el-button type="text" @click="goOrders(scope.row)">查看订单</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        class="life-pagination"
        :current-page.sync="page.current"
        :page-size="page.size"
        :page-sizes="[10, 20, 50]"
        :total="page.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="changeSize"
        @current-change="load"
      />
    </section>

    <el-dialog title="商品预览" :visible.sync="previewVisible" width="640px">
      <div v-if="previewRow">
        <p><strong>{{ previewRow.productName }}</strong></p>
        <p class="life-muted">{{ previewRow.subtitle }}</p>
        <el-image v-if="previewRow.coverUrl" :src="previewRow.coverUrl" style="width: 100%; max-height: 240px" fit="cover" />
        <el-image v-if="previewRow.detailImageUrl" :src="previewRow.detailImageUrl" style="width: 100%; margin-top: 12px" fit="contain" />
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { listProducts, publishProduct, offlineProduct, copyProduct } from '@/api/travel/travelApi'

export default {
  name: 'TravelProductList',
  data() {
    return {
      loading: false,
      rows: [],
      filters: { productName: '', productType: '', saleStatus: '', recommended: '' },
      page: { current: 1, size: 10, total: 0 },
      previewVisible: false,
      previewRow: null
    }
  },
  created() {
    this.load()
  },
  methods: {
    fenToYuan(fen) {
      if (fen === null || fen === undefined || fen === '') return '-'
      return (Number(fen) / 100).toFixed(2)
    },
    typeLabel(type) {
      return type === 'ROUTE' ? '线路' : type === 'SCENIC' ? '景区' : type
    },
    statusLabel(status) {
      return ({ DRAFT: '草稿', ON_SALE: '已上架', OFF_SALE: '已下架', ARCHIVED: '已归档' })[status] || status
    },
    statusTag(status) {
      return ({ DRAFT: 'info', ON_SALE: 'success', OFF_SALE: 'warning', ARCHIVED: '' })[status] || 'info'
    },
    search() {
      this.page.current = 1
      this.load()
    },
    reset() {
      this.filters = { productName: '', productType: '', saleStatus: '', recommended: '' }
      this.search()
    },
    changeSize(size) {
      this.page.size = size
      this.page.current = 1
      this.load()
    },
    async load() {
      this.loading = true
      try {
        const res = await listProducts({
          ...this.filters,
          page: this.page.current,
          row: this.page.size
        })
        this.rows = res.data || []
        this.page.total = res.total || 0
      } catch (e) {
        this.$message.error(e.message || '加载失败')
      } finally {
        this.loading = false
      }
    },
    goEdit(row) {
      this.$router.push({
        path: '/travel/productEdit',
        query: row && row.productId ? { productId: row.productId } : {}
      })
    },
    goOrders(row) {
      this.$router.push({
        path: '/travel/orderList',
        query: { productName: row.productName }
      })
    },
    preview(row) {
      this.previewRow = row
      this.previewVisible = true
    },
    async handlePublish(row) {
      try {
        await publishProduct({ productId: row.productId })
        this.$message.success('上架成功')
        this.load()
      } catch (e) {
        this.$message.error(e.message || '上架失败')
      }
    },
    async handleOffline(row) {
      try {
        await this.$confirm('下架后用户端不可新下单，历史订单仍可履约。确认下架？', '提示')
        await offlineProduct({ productId: row.productId })
        this.$message.success('下架成功')
        this.load()
      } catch (e) {
        if (e !== 'cancel') this.$message.error(e.message || '下架失败')
      }
    },
    async handleCopy(row) {
      try {
        const res = await copyProduct({ productId: row.productId })
        this.$message.success('已复制为草稿')
        this.goEdit({ productId: res.data.productId })
      } catch (e) {
        this.$message.error(e.message || '复制失败')
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import './life.scss';
</style>
